import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-cta-button',
  templateUrl: './cta-button.component.html',
  styleUrls: ['./cta-button.component.scss'],
  standalone: false,
})
export class CtaButtonComponent implements OnChanges {
  @ViewChild('ctaBtn', { static: false }) ctaBtn!: ElementRef<HTMLButtonElement>;

  /** Translation key for the button label */
  @Input() label: string = '';

  /** Translation key shown while loading; falls back to label if not set */
  @Input() loadingLabel: string = '';

  /** PrimeIcons class without the "pi " prefix, e.g. "pi-arrow-right" */
  @Input() icon: string = 'pi-arrow-right';

  /** Externally controlled disabled state (loading also disables the button) */
  @Input() disabled: boolean = false;

  /** Shows spinner and disables button while an async action is in progress */
  @Input() loading: boolean = false;

  /** Controls whether success feedback should move focus to the CTA automatically. */
  @Input() autoFocusOnSuccess: boolean = true;

  /**
   * Set to true when the current step is complete and the user should proceed.
   * Triggers auto-focus, scroll-into-view, and a subtle shake animation.
   */
  @Input() successState: boolean = false;

  /** Emits when the button is clicked and is not disabled/loading */
  @Output() buttonClick = new EventEmitter<void>();

  get isDisabled(): boolean {
    return this.disabled || this.loading;
  }

  get resolvedLabel(): string {
    return this.loading && this.loadingLabel ? this.loadingLabel : this.label;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['successState']?.currentValue === true) {
      this.triggerSuccessFeedback();
    }
  }

  handleClick(): void {
    if (!this.isDisabled) {
      this.buttonClick.emit();
    }
  }

  /** Focuses and animates the button — can also be called by parent components */
  triggerSuccessFeedback(): void {
    setTimeout(() => {
      const btn = this.ctaBtn?.nativeElement;
      if (!btn) return;

      if (this.autoFocusOnSuccess) {
        btn.focus();
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      btn.classList.add('shake-gently');
      setTimeout(() => btn.classList.remove('shake-gently'), 600);
    }, 100);
  }
}
